import React, { useState } from 'react';
import { View, StyleSheet, ScrollView, Linking, Alert } from 'react-native';
import { Text, Button, Card, Divider, useTheme } from 'react-native-paper';
import { MaterialIcons } from '@expo/vector-icons';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionService } from '../services/subscriptionService';
import { logger } from '../utils/logger';

const PaywallScreen: React.FC = () => {
  const theme = useTheme();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleLogout = async (): Promise<void> => {
    try {
      await logout();
    } catch (error) {
      logger.error('Failed to log out from paywall', error);
      Alert.alert('Logout Error', 'Unable to log out. Please try again.', [{ text: 'OK' }]);
    }
  };

  const handleSubscribe = async (): Promise<void> => {
    try {
      setLoading(true);
      logger.info('Starting subscription flow from paywall');

      const { url } = await subscriptionService.createCheckoutSession(
        'https://oceaninsightpromobile.web.app/',
        'https://oceaninsightpromobile.web.app/'
      );

      logger.info('Opening Stripe Checkout:', url);

      const supported = await Linking.canOpenURL(url);
      if (supported) {
        await Linking.openURL(url);
      } else {
        throw new Error('Unable to open checkout page');
      }
    } catch (error) {
      logger.error('Failed to start subscription', error);
      Alert.alert(
        'Subscription Error',
        'Failed to start subscription process. Please try again.',
        [{ text: 'OK' }]
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <View style={styles.header}>
        <Button
          mode="text"
          icon="logout"
          onPress={handleLogout}
          compact
          style={styles.logoutButton}
        >
          Log out
        </Button>
        <MaterialIcons name="waves" size={80} color={theme.colors.primary} />
        <Text style={styles.title}>Ocean Insight Pro</Text>
        <Text style={styles.subtitle}>Unlock Premium Fishing Intelligence</Text>
      </View>

      <Card style={styles.card}>
        <Card.Content>
          <Text style={styles.sectionTitle}>Premium Features</Text>

          <View style={styles.featuresList}>
            <View style={styles.featureItem}>
              <MaterialIcons name="check-circle" size={24} color="#4CAF50" />
              <Text style={styles.featureText}>AI-Powered Hotspot Predictions</Text>
            </View>

            <View style={styles.featureItem}>
              <MaterialIcons name="check-circle" size={24} color="#4CAF50" />
              <Text style={styles.featureText}>Near Real-Time Satellite Oceanographic Data</Text>
            </View>

            <View style={styles.featureItem}>
              <MaterialIcons name="check-circle" size={24} color="#4CAF50" />
              <Text style={styles.featureText}>SST, Chlorophyll & Current Analysis</Text>
            </View>

            <View style={styles.featureItem}>
              <MaterialIcons name="check-circle" size={24} color="#4CAF50" />
              <Text style={styles.featureText}>Advanced Weather Forecasts</Text>
            </View>

            <View style={styles.featureItem}>
              <MaterialIcons name="check-circle" size={24} color="#4CAF50" />
              <Text style={styles.featureText}>Unlimited Catch Reports & Trip Planning</Text>
            </View>

            <View style={styles.featureItem}>
              <MaterialIcons name="check-circle" size={24} color="#4CAF50" />
              <Text style={styles.featureText}>GPX Export for Marine Electronics</Text>
            </View>
          </View>

          <Divider style={styles.divider} />

          <View style={styles.pricingContainer}>
            <Text style={styles.priceText}>$24.99/month</Text>
            <Text style={styles.priceSubtext}>Cancel anytime • No commitment</Text>
          </View>

          <Button
            mode="contained"
            onPress={handleSubscribe}
            loading={loading}
            disabled={loading}
            style={styles.subscribeButton}
            contentStyle={styles.subscribeButtonContent}
          >
            Start Your Subscription
          </Button>

          <Text style={styles.disclaimer}>
            By subscribing, you agree to automatic recurring billing until canceled.
            You can manage or cancel your subscription anytime.
          </Text>
        </Card.Content>
      </Card>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  content: {
    padding: 16,
    paddingBottom: 32,
  },
  header: {
    alignItems: 'center',
    marginBottom: 24,
    marginTop: 16,
  },
  logoutButton: {
    alignSelf: 'flex-end',
    marginBottom: 8,
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
  },
  card: {
    marginBottom: 16,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 16,
    textAlign: 'center',
  },
  featuresList: {
    marginBottom: 16,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  featureText: {
    fontSize: 16,
    marginLeft: 12,
    flex: 1,
  },
  divider: {
    marginVertical: 20,
  },
  pricingContainer: {
    alignItems: 'center',
    marginBottom: 24,
  },
  priceText: {
    fontSize: 36,
    fontWeight: 'bold',
    color: '#2196F3',
  },
  priceSubtext: {
    fontSize: 14,
    color: '#666',
    marginTop: 4,
  },
  subscribeButton: {
    marginBottom: 16,
  },
  subscribeButtonContent: {
    paddingVertical: 8,
  },
  disclaimer: {
    fontSize: 12,
    color: '#999',
    textAlign: 'center',
    lineHeight: 18,
  },
});

export default PaywallScreen;
